import { cn } from "@/lib/utils";
import { Plant, Tree, FlowerLotus, Fire, CheckCircle, Circle, CircleDashed } from "@phosphor-icons/react";

/* ---------------------------------- Ring ----------------------------------------- */
const ringStroke = { primary: "stroke-primary", teal: "stroke-teal", blue: "stroke-blue", secondary: "stroke-secondary" };

export function ProgressRing({ value = 0, size = 96, stroke = 9, color = "primary", children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} className="fill-none stroke-muted" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className={cn("fill-none transition-[stroke-dashoffset] duration-700 ease-out", ringStroke[color])}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (Math.max(0, Math.min(100, value)) / 100) * c}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
    </div>
  );
}

/* -------------------------------- Mastery levels --------------------------------- */
export const MASTERY = {
  seed: { level: 1, label: "Seed", icon: Plant, text: "text-muted-foreground", tint: "bg-muted", fill: "bg-muted-foreground/50", desc: "Just planted" },
  growing: { level: 2, label: "Growing", icon: Plant, text: "text-teal-ink", tint: "bg-teal/15", fill: "bg-teal", desc: "Taking root" },
  strong: { level: 3, label: "Strong", icon: Tree, text: "text-blue-ink", tint: "bg-blue/12", fill: "bg-blue", desc: "Standing tall" },
  mastered: { level: 4, label: "Mastered", icon: FlowerLotus, text: "text-primary-strong", tint: "bg-primary/12", fill: "bg-primary", desc: "In full bloom" },
};

function GrowthDots({ level, fill }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {[1, 2, 3, 4].map((n) => (
        <span key={n} className={cn("h-2.5 rounded-full transition-all", n <= level ? `w-6 ${fill}` : "w-2.5 bg-muted")} />
      ))}
    </div>
  );
}

export function MasteryBadge({ level = "growing" }) {
  const m = MASTERY[level];
  return (
    <span className={cn("inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold", m.tint, m.text)}>
      <m.icon size={16} weight="fill" />
      {m.label}
    </span>
  );
}

export function MasteryCard({ subjectLabel = "Multiplication", level = "strong" }) {
  const m = MASTERY[level];
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <div className="flex items-center gap-4">
        <span className={cn("flex h-16 w-16 items-center justify-center rounded-2xl", m.tint, m.text)}>
          <m.icon size={34} weight="fill" />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{subjectLabel}</p>
          <p className={cn("font-display text-2xl font-extrabold", m.text)}>{m.label}</p>
          <p className="text-sm font-semibold text-muted-foreground">{m.desc}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <GrowthDots level={m.level} fill={m.fill} />
        <span className="text-sm font-bold text-muted-foreground">Stage {m.level} / 4</span>
      </div>
    </div>
  );
}

/* --------------------------------- Streak Card ----------------------------------- */
export function StreakCard({ days = 5 }) {
  const week = ["M", "T", "W", "T", "F", "S", "S"];
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <div className="flex items-center gap-3">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/12 text-primary-strong">
          <Fire size={30} weight="fill" />
        </span>
        <div>
          <p className="font-display text-3xl font-black leading-none">{days} days</p>
          <p className="text-sm font-bold text-muted-foreground">Learning streak</p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between">
        {week.map((d, i) => {
          const on = i < days % 7 || (days >= 7 && i < 7);
          return (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <span className={cn("flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold", on ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                {on ? <Fire size={15} weight="fill" /> : d}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------------------- Completion Indicator ------------------------------ */
export function CompletionIndicator({ state = "in-progress", label }) {
  const map = {
    "not-started": { icon: CircleDashed, cls: "text-muted-foreground", text: label || "Not started" },
    "in-progress": { icon: Circle, cls: "text-blue-ink", text: label || "In progress" },
    complete: { icon: CheckCircle, cls: "text-teal-ink", text: label || "Complete" },
  };
  const s = map[state];
  return (
    <span className={cn("inline-flex items-center gap-2 text-sm font-bold", s.cls)}>
      <s.icon size={20} weight={state === "in-progress" ? "bold" : "fill"} />
      {s.text}
    </span>
  );
}
